import { useEffect, useRef, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { QRCodeCanvas } from "qrcode.react";
import { Button } from "@/components/ui/button";
import { Check, Bell, Calendar as CalIcon, MapPin, Phone, Mail, Loader2, CreditCard, RefreshCcw, Download } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { requestNotificationPermission, sendLocalNotification, subscribeToPush } from "@/lib/push";
import { STAGING_LOCATIONS } from "@/lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const titleCase = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : "");
const stagingLabel = (id) => STAGING_LOCATIONS.find((s) => s.id === id)?.label || titleCase(id);

export default function ConfirmationPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notifState, setNotifState] = useState("idle"); // idle | granted | denied | unsupported
  const [payState, setPayState] = useState(sessionId ? "polling" : "skip"); // polling | paid | failed | expired | skip
  const pollRef = useRef(null);

  const fetchBooking = () =>
    axios
      .get(`${API}/bookings/${id}`)
      .then((r) => setBooking(r.data))
      .catch(() => toast.error("Could not load booking"));

  useEffect(() => {
    fetchBooking().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Poll payment status if we came back from Stripe with a session_id
  useEffect(() => {
    if (!sessionId) return;
    let attempts = 0;
    const maxAttempts = 10;
    const poll = async () => {
      attempts += 1;
      try {
        const res = await axios.get(`${API}/checkout/status/${sessionId}`);
        const { payment_status, status } = res.data;
        if (payment_status === "paid") {
          setPayState("paid");
          toast.success("Payment received — you're all set!");
          await fetchBooking();
          return;
        }
        if (status === "expired" || payment_status === "expired") {
          setPayState("expired");
          toast.error("Payment session expired. Please retry.");
          return;
        }
        if (attempts >= maxAttempts) {
          // Couldn't confirm within window — show processing state (likely webhook will catch up)
          setPayState("processing");
          return;
        }
        pollRef.current = setTimeout(poll, 2000);
      } catch {
        if (attempts >= maxAttempts) {
          setPayState("processing");
          return;
        }
        pollRef.current = setTimeout(poll, 2000);
      }
    };
    poll();
    return () => clearTimeout(pollRef.current);
  }, [sessionId]);

  // Auto-prompt for browser notification permission (only after payment confirmed or when no payment needed)
  useEffect(() => {
    if (!booking) return;
    if (payState === "polling") return;
    if (typeof window === "undefined" || !("Notification" in window)) {
      setNotifState("unsupported");
      return;
    }
    if (Notification.permission === "granted") {
      setNotifState("granted");
      sendLocalNotification(
        `Booking confirmed #${booking.order_number}`,
        `Pickup ${booking.pickup_date} • ${booking.pickup_time_slot}`,
      );
    }
  }, [booking, payState]);

  const enablePush = async () => {
    if (!booking) return;
    const { status } = await subscribeToPush(booking.id);
    setNotifState(status);
    if (status === "granted") {
      sendLocalNotification(
        `Booking confirmed #${booking.order_number}`,
        `We'll ping you on pickup day — ${booking.pickup_date} • ${booking.pickup_time_slot}`,
      );
      toast.success("Browser notifications enabled — we'll ping you for pickup updates.");
    } else if (status === "denied") {
      toast.error("Notifications blocked. Enable them in your browser settings.");
    } else if (status === "unsupported") {
      toast.error("Your browser doesn't support push notifications.");
    } else {
      toast.error("Couldn't enable notifications. Please try again.");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-white">
        <Loader2 className="animate-spin text-[#ff6b35]" size={32} />
      </div>
    );
  }
  if (!booking) {
    return (
      <div className="min-h-screen flex flex-col bg-white">
        <Navbar />
        <main className="flex-1 grid place-items-center text-center p-10">
          <div>
            <h1 className="font-display text-3xl font-extrabold">Booking not found</h1>
            <p className="text-[#333] mt-2">The link may be incorrect or the booking expired.</p>
            <Link to="/">
              <Button className="mt-5 bg-[#ff6b35] hover:bg-[#ff8c42] text-white rounded-lg font-bold">Go home</Button>
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div data-testid="confirmation-page" className="bg-white min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1">
        <div className="hero-gradient text-white">
          <div className="max-w-3xl mx-auto px-4 py-14 text-center">
            <div className="inline-grid place-items-center w-16 h-16 rounded-full bg-white/20 backdrop-blur mb-4">
              {payState === "polling" ? (
                <Loader2 size={28} className="animate-spin" />
              ) : (
                <Check size={32} strokeWidth={3} />
              )}
            </div>
            <h1 className="font-display text-4xl sm:text-5xl font-black">
              {payState === "polling" ? "Confirming payment…" : "Booking confirmed!"}
            </h1>
            <p className="mt-3 text-white/90 text-lg">
              {payState === "polling"
                ? "Hang tight — this only takes a few seconds."
                : `We'll see you soon, ${titleCase(booking.customer_name.split(" ")[0])}.`}
            </p>
            <div className="mt-5 inline-block bg-white text-[#1a1a1a] px-5 py-2 rounded-full font-bold text-sm">
              Order # <span data-testid="order-number" className="text-[#ff6b35]">{booking.order_number}</span>
            </div>
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-4 py-10">
          {/* Payment status banner */}
          {payState === "paid" && (
            <div data-testid="payment-paid" className="mb-6 bg-[#fffbf0] border-l-[5px] border-[#ff6b35] rounded-r-xl p-4 flex items-center gap-3">
              <CreditCard size={20} className="text-[#ff6b35] flex-shrink-0" />
              <div className="flex-1 text-sm text-[#1a1a1a]">
                <span className="font-bold">Paid in full</span> — receipt sent to {booking.email}.
              </div>
            </div>
          )}
          {payState === "polling" && (
            <div className="mb-6 bg-[#fffbf0] border-l-[5px] border-[#ff6b35] rounded-r-xl p-4 flex items-center gap-3">
              <Loader2 size={20} className="text-[#ff6b35] animate-spin flex-shrink-0" />
              <div className="flex-1 text-sm text-[#1a1a1a]">
                <span className="font-bold">Verifying payment…</span> Stripe is confirming the charge.
              </div>
            </div>
          )}
          {(payState === "failed" || payState === "expired") && (
            <div data-testid="payment-failed" className="mb-6 bg-white border-2 border-[#ff6b35] rounded-xl p-4 flex items-center gap-3">
              <CreditCard size={20} className="text-[#ff6b35] flex-shrink-0" />
              <div className="flex-1 text-sm text-[#1a1a1a]">
                <span className="font-bold">Payment didn't go through.</span>{" "}
                <Link to={`/book?tier=${booking.tier}`} className="text-[#ff6b35] font-bold hover:underline">Try again →</Link>
              </div>
            </div>
          )}
          <div className="bg-white border-[3px] border-[#ff6b35] rounded-xl p-6 sm:p-8 shadow-sm">
            <h2 className="font-display text-2xl font-extrabold">Pickup details</h2>
            <div className="mt-5 grid sm:grid-cols-2 gap-5">
              <Detail icon={<CalIcon size={18} className="text-[#ff6b35]" />} label="When">
                {booking.pickup_date}
                <br />
                {booking.pickup_time_slot}
              </Detail>
              <Detail icon={<MapPin size={18} className="text-[#ff6b35]" />} label="Where">
                {booking.address}
                <br />
                {booking.city}, OH {booking.zip_code}
                <br />
                <span className="text-[#333] text-sm">Stage at: {stagingLabel(booking.staging_location)}</span>
              </Detail>
              <Detail icon={<Mail size={18} className="text-[#ff6b35]" />} label="Email">
                {booking.email}
              </Detail>
              <Detail icon={<Phone size={18} className="text-[#ff6b35]" />} label="Phone">
                {booking.phone}
              </Detail>
            </div>
            <div className="mt-6 pt-6 border-t border-[#1a1a1a]/10 flex justify-between items-baseline">
              <span className="text-[#333] text-sm font-bold uppercase tracking-wider">Total</span>
              <span data-testid="confirm-total" className="font-display text-4xl font-black text-[#ff6b35]">
                ${booking.total_price.toFixed(0)}
              </span>
            </div>
            <p className="text-xs text-[#333] mt-2 text-right">Paid securely via Stripe. Receipt emailed automatically.</p>
          </div>

          {/* Push notification prompt */}
          <div className="mt-6 bg-[#fffbf0] border-l-[5px] border-[#ff6b35] rounded-r-xl p-5 flex items-start gap-4">
            <Bell size={22} className="text-[#ff6b35] mt-1 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="font-display font-bold text-lg">Get pickup day updates</h3>
              <p className="text-[#333] text-sm mt-1">
                Opt in to browser notifications and we'll ping you the day before and when the crew is on the way. Free, no phone number needed.
              </p>
              <div className="mt-3">
                {notifState === "granted" ? (
                  <span data-testid="notif-enabled" className="inline-flex items-center gap-1 text-sm font-bold text-[#ff6b35]">
                    <Check size={14} /> Notifications enabled — we'll ping you for updates
                  </span>
                ) : notifState === "unsupported" ? (
                  <span className="text-sm text-[#333]">Your browser doesn't support web push.</span>
                ) : notifState === "denied" ? (
                  <span className="text-sm text-[#333]">Notifications blocked. Enable them in your browser settings to get updates.</span>
                ) : (
                  <Button
                    data-testid="enable-notif-btn"
                    onClick={enablePush}
                    className="bg-[#ff6b35] hover:bg-[#ff8c42] text-white rounded-lg font-bold"
                  >
                    Enable notifications
                  </Button>
                )}
              </div>
            </div>
          </div>

          <div className="mt-6 bg-[#1a1a1a] text-white rounded-xl p-6">
            <h3 className="font-display font-bold text-xl">What to expect</h3>
            <ul className="mt-3 text-white/80 space-y-2 text-sm">
              <li>• We'll arrive during your scheduled time window.</li>
              <li>• A confirmation email is on the way (if provided).</li>
              <li>• You don't need to be home — just stage the items.</li>
              <li>• Your card has been charged — Stripe sends a receipt automatically.</li>
            </ul>
            <p className="text-white/70 text-xs mt-4">
              Questions? Call or text (216) 306-2330.
            </p>
          </div>

          {/* BOOK AGAIN — QR code + one-tap link for repeat customers */}
          <BookAgainBlock booking={booking} />

          <div className="mt-8 text-center">
            <Link to="/" data-testid="back-home-link" className="text-[#ff6b35] font-bold hover:underline">
              ← Back to home
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function Detail({ icon, label, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#333]">
        {icon} {label}
      </div>
      <div className="mt-1 text-[#1a1a1a] font-medium">{children}</div>
    </div>
  );
}

function BookAgainBlock({ booking }) {
  const qrRef = useRef(null);
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const rebookUrl = `${origin}/book?rebook=${booking.id}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(rebookUrl);
      toast.success("Link copied");
    } catch {
      toast.error("Copy failed — long-press the QR to save instead");
    }
  };

  const downloadQR = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (!canvas) return;
    const url = canvas.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = url;
    a.download = `cpmt-book-again-${booking.order_number}.png`;
    a.click();
  };

  return (
    <div data-testid="book-again-block" className="mt-6 bg-white rounded-xl border-[3px] border-[#ff6b35] overflow-hidden">
      <div className="bg-[#fffbf0] px-5 sm:px-6 py-4 border-b border-[#ff6b35]/30">
        <div className="flex items-center gap-2">
          <RefreshCcw size={18} className="text-[#ff6b35]" />
          <h3 className="font-display font-extrabold text-lg sm:text-xl">Book again in one tap</h3>
        </div>
        <p className="text-sm text-[#333] mt-1">
          Save this QR code. Next time you need a pickup, just scan it — we'll pre-fill your info and you'll be done in 30 seconds.
        </p>
      </div>
      <div className="p-5 sm:p-6 flex flex-col sm:flex-row items-center gap-5">
        <div ref={qrRef} className="bg-white p-3 rounded-lg border-2 border-[#1a1a1a]/10 flex-shrink-0">
          <QRCodeCanvas
            data-testid="book-again-qr"
            value={rebookUrl}
            size={144}
            bgColor="#ffffff"
            fgColor="#1a1a1a"
            level="M"
            includeMargin={false}
          />
        </div>
        <div className="flex-1 min-w-0 text-center sm:text-left">
          <div className="text-xs font-bold text-[#ff6b35] uppercase tracking-widest">Your rebook link</div>
          <div className="text-xs text-[#333] mt-1 font-mono break-all truncate">{rebookUrl}</div>
          <div className="mt-4 flex flex-col sm:flex-row gap-2">
            <Link
              to={`/book?rebook=${booking.id}`}
              data-testid="book-again-btn"
              className="inline-flex items-center justify-center gap-2 bg-[#ff6b35] hover:bg-[#ff8c42] text-white font-bold rounded-lg px-4 py-2.5 text-sm transition-colors"
            >
              <RefreshCcw size={14} /> Book again now
            </Link>
            <Button
              data-testid="copy-rebook-link"
              onClick={copyLink}
              variant="outline"
              className="border-2 border-[#1a1a1a]/20 font-bold text-sm"
            >
              Copy link
            </Button>
            <Button
              data-testid="download-qr"
              onClick={downloadQR}
              variant="outline"
              className="border-2 border-[#1a1a1a]/20 font-bold text-sm"
            >
              <Download size={14} className="mr-1" /> Save QR
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

